/**
 * [센서상태 및 에러처리]
 * 두자리씩 다섯개, 10자리 문자열로 구분하여 처리한다.
 * 00(센서상태)00(파워에러)00(LM에러)00(스윙카메라 전면 에러)00(스윙카메라 후면 에러)
 */

abstract class MachineSensor {
  _stateCode: string

  constructor(state: string, start: number, end: number) {
    this._stateCode = state?.slice(start, end) || ''
  }

  get isError() {
    return this._stateCode != '00'
  }

  get noResponse() {
    return 'seat_sensor_null'
  }

  abstract get name(): string

  abstract get code(): string
}

class MachineSensorState extends MachineSensor {
  get name() {
    return 'seat_state_sensor'
  }

  get code() {
    switch (this._stateCode) {
    case '00':
      return 'on'
    case '01':
      return 'off'
    case '02':
      return 'sleep'
    case '03':
      return 'error'
    default:
      return this.noResponse
    }
  }

  get isError() {
    return this._stateCode == '03'
  }

  get isAlive() {
    return this._stateCode == '00' || this._stateCode == '02'
  }
}

class MachinePowerError extends MachineSensor {
  get name() {
    return 'seat_state_power'
  }

  get code() {
    switch (this._stateCode) {
    case '00':
      return 'POWER_SUCCESS'
    case '01':
      return 'POWER_ERROR'
    default:
      return this.noResponse
    }
  }
}

class MachineLMError extends MachineSensor {
  get name() {
    return 'seat_state_lmsensor'
  }

  get code() {
    switch (this._stateCode) {
    case '00':
      return 'LM_SUCCESS'
    case '01':
      return 'LM_ERROR_USB'
    case '02':
      return 'LM_ERROR_FPGA_REGISTER'
    case '03':
      return 'LM_ERROR_FPGA_MEMORY'
    case '04':
      return 'LM_ERROR_CAMERA_S0'
    case '05':
      return 'LM_ERROR_CAMERA_S1'
    case '06':
      return 'LM_ERORR_CAMERA_SC'
    default:
      return this.noResponse
    }
  }
}

class MachineSwingFrontError extends MachineSensor {
  get name() {
    return 'seat_state_swf'
  }

  get code() {
    switch (this._stateCode) {
    case '00':
      return 'SWF_SUCCESS'
    case '01':
      return 'SWF_ERROR_USB'
    case '02':
      return 'SWF_ERROR_FPGA_REGISTER'
    case '03':
      return 'SWF_ERROR_FPGA_MEMORY'
    case '04':
      return 'SWF_ERROR_CAMERA'
    default:
      return this.noResponse
    }
  }
}

class MachineSwingBackError extends MachineSensor {
  get name() {
    return 'seat_state_swb'
  }

  get code() {
    switch (this._stateCode) {
    case '00':
      return 'SWB_SUCCESS'
    case '01':
      return 'SWB_ERROR_USB'
    case '02':
      return 'SWB_ERROR_FPGA_REGISTER'
    case '03':
      return 'SWB_ERROR_FPGA_MEMORY'
    case '04':
      return 'SWB_ERROR_CAMERA'
    default:
      return this.noResponse
    }
  }
}

export class MachineSensorManager {
  constructor(state: string) {
    this._errors = [
      new MachinePowerError(state, 2, 4),
      new MachineLMError(state, 4, 6),
      new MachineSwingFrontError(state, 6, 8),
      new MachineSwingBackError(state, 8, 10),
    ]

    this._sensorState = new MachineSensorState(state, 0, 2)
  }

  _errors: MachineSensor[]

  get errors() {
    return this._errors
  }

  _sensorState: MachineSensorState

  get sensorState() {
    return this._sensorState
  }

  get hasError() {
    return this.errors.some((error) => error.isError)
  }

  get isNotAvailable() {
    return (
      !this._sensorState.isAlive ||
      this._errors?.[0]?.isError ||
      this._errors?.[1]?.isError
    )
  }
}
